import React from 'react';
import { GraduationCap, Building, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';

const PartnerRow = ({ partnerKey, partner, onToggle, onChangeDiscount, onDelete }) => (
  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
    <Switch checked={partner.enabled} onCheckedChange={(v) => onToggle(partnerKey, v)} />
    <div className="flex-1">
      <p className="font-medium text-sm">{partner.name}</p>
      <p className="text-xs text-gray-500">Code: {partner.code}</p>
    </div>
    <div className="flex items-center gap-2">
      <Input type="number" className="w-16 h-8 text-sm" value={partner.discount_percent} onChange={(e) => onChangeDiscount(partnerKey, parseFloat(e.target.value))} min="0" max="100" />
      <span className="text-sm">%</span>
      <Button variant="ghost" size="sm" className="text-red-500 h-8 w-8 p-0" onClick={() => onDelete(partnerKey)}>
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  </div>
);

const AddPartnerForm = ({ idPrefix, defaultDiscount, onAdd }) => {
  const handleAdd = () => {
    const name = document.getElementById(`${idPrefix}-name`).value;
    const code = document.getElementById(`${idPrefix}-code`).value;
    const discount = parseFloat(document.getElementById(`${idPrefix}-discount`).value) || defaultDiscount;
    if (name && code) {
      onAdd(name, code, discount);
      document.getElementById(`${idPrefix}-name`).value = '';
      document.getElementById(`${idPrefix}-code`).value = '';
      document.getElementById(`${idPrefix}-discount`).value = String(defaultDiscount);
    }
  };

  return (
    <div className="border-t pt-4 mt-4">
      <p className="text-sm font-medium mb-3">Add New Partner</p>
      <div className="grid grid-cols-3 gap-2">
        <Input placeholder="Name" id={`${idPrefix}-name`} className="text-sm" />
        <Input placeholder="Discount code" id={`${idPrefix}-code`} className="text-sm" />
        <div className="flex gap-2">
          <Input type="number" placeholder="%" id={`${idPrefix}-discount`} className="w-16 text-sm" defaultValue={String(defaultDiscount)} />
          <Button size="sm" onClick={handleAdd}><Plus className="h-4 w-4" /></Button>
        </div>
      </div>
    </div>
  );
};

export const UniversityPartnerSettings = ({ settings, updateSetting }) => {
  const partners = settings?.university_discounts || {};

  const handleToggle = (key, enabled) => {
    const updated = { ...partners, [key]: { ...partners[key], enabled } };
    updateSetting('university_discounts', updated);
  };

  const handleChangeDiscount = (key, discount_percent) => {
    const updated = { ...partners, [key]: { ...partners[key], discount_percent } };
    updateSetting('university_discounts', updated);
  };

  const handleDelete = (key) => {
    const updated = { ...partners };
    delete updated[key];
    updateSetting('university_discounts', updated);
  };

  const handleAdd = (name, code, discount) => {
    const key = name.toLowerCase().replace(/\s+/g, '-');
    updateSetting('university_discounts', {
      ...partners,
      [key]: { name, code: code.toUpperCase(), discount_percent: discount, enabled: true }
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <GraduationCap className="h-5 w-5 text-blue-600" />
          University Partner Discounts
        </CardTitle>
        <CardDescription>Configure student discounts for partner universities</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <Label htmlFor="uni-partners">Enable University Partners</Label>
          <Switch id="uni-partners" checked={settings?.university_partners_enabled} onCheckedChange={(v) => updateSetting('university_partners_enabled', v)} />
        </div>
        {Object.entries(partners).map(([key, uni]) => (
          <PartnerRow key={key} partnerKey={key} partner={uni} onToggle={handleToggle} onChangeDiscount={handleChangeDiscount} onDelete={handleDelete} />
        ))}
        <AddPartnerForm idPrefix="new-uni" defaultDiscount={15} onAdd={handleAdd} />
      </CardContent>
    </Card>
  );
};

export const BusinessPartnerSettings = ({ settings, updateSetting }) => {
  const partners = settings?.business_partner_discounts || {};

  const handleToggle = (key, enabled) => {
    const updated = { ...partners, [key]: { ...partners[key], enabled } };
    updateSetting('business_partner_discounts', updated);
  };

  const handleChangeDiscount = (key, discount_percent) => {
    const updated = { ...partners, [key]: { ...partners[key], discount_percent } };
    updateSetting('business_partner_discounts', updated);
  };

  const handleDelete = (key) => {
    const updated = { ...partners };
    delete updated[key];
    updateSetting('business_partner_discounts', updated);
  };

  const handleAdd = (name, code, discount) => {
    const key = name.toLowerCase().replace(/\s+/g, '-');
    updateSetting('business_partner_discounts', {
      ...partners,
      [key]: { name, code: code.toUpperCase(), discount_percent: discount, enabled: true }
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Building className="h-5 w-5 text-purple-600" />
          Business Partner Discounts
        </CardTitle>
        <CardDescription>Configure discounts for business partners</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <Label htmlFor="partners-enabled">Enable Partner Pages</Label>
          <Switch id="partners-enabled" checked={settings?.partners_enabled} onCheckedChange={(v) => updateSetting('partners_enabled', v)} />
        </div>
        {Object.entries(partners).map(([key, partner]) => (
          <PartnerRow key={key} partnerKey={key} partner={partner} onToggle={handleToggle} onChangeDiscount={handleChangeDiscount} onDelete={handleDelete} />
        ))}
        <AddPartnerForm idPrefix="new-biz" defaultDiscount={10} onAdd={handleAdd} />
      </CardContent>
    </Card>
  );
};
